import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

/** A grouped single-select with the same interaction contract as ui/Select. */
const CategoryPicker = ({ label, name, value, onChange, categories = [], placeholder = 'Выберите категорию', allowEmpty = false, emptyLabel = 'Все категории', disabled, required, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const reactId = useId().replace(/:/g, '');
  const labelId = `category-picker-${reactId}-label`;
  const listId = `category-picker-${reactId}-listbox`;

  const parents = categories.filter((category) => !category.parent_id);
  const childrenByParent = {};
  categories.forEach((category) => {
    if (category.parent_id) (childrenByParent[category.parent_id] ??= []).push(category);
  });
  // PERS2-83: пикер может получить ТОЛЬКО дочерние категории (каскадный выбор
  // частей разбивки) — тогда parents пуст, и список строится плоским из
  // переданных категорий, иначе родители группируются со своими детьми.
  const items = [
    ...(allowEmpty ? [{ id: '', label: emptyLabel, empty: true }] : []),
    ...(parents.length > 0
      ? parents.flatMap((parent) => [
          { id: parent.id, label: parent.name, parent: true, hasChildren: (childrenByParent[parent.id] ?? []).length > 0 },
          ...(childrenByParent[parent.id] ?? []).map((child) => ({ id: child.id, label: child.name, child: true }))
        ])
      : categories.map((category) => ({ id: category.id, label: category.name, child: true })))
  ];
  const normalizedValue = value != null ? String(value) : '';
  const selectedIndex = items.findIndex((item) => String(item.id) === normalizedValue);
  const selectedCategory = normalizedValue ? categories.find((category) => String(category.id) === normalizedValue) : null;
  const selectedParent = selectedCategory?.parent_id ? categories.find((category) => String(category.id) === String(selectedCategory.parent_id)) : null;
  const displayText = selectedCategory ? (selectedParent ? `${selectedParent.name} / ${selectedCategory.name}` : selectedCategory.name) : placeholder;

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  useEffect(() => {
    if (!isOpen || !items.length) return;
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    listRef.current?.focus();
  }, [isOpen, selectedIndex, items.length]);

  const close = (focusTrigger = false) => {
    setIsOpen(false);
    if (focusTrigger) requestAnimationFrame(() => triggerRef.current?.focus());
  };
  const emit = (id) => {
    onChange?.({ target: { name: name ?? '', value: id != null ? String(id) : '' } });
    close(true);
  };
  const handleKeyDown = (event) => {
    if (disabled) return;
    if (!isOpen && ['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      setIsOpen(true);
      return;
    }
    if (!isOpen) return;
    if (event.key === 'ArrowDown') { event.preventDefault(); setActiveIndex((index) => (index + 1) % items.length); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActiveIndex((index) => (index - 1 + items.length) % items.length); }
    else if (event.key === 'Home') { event.preventDefault(); setActiveIndex(0); }
    else if (event.key === 'End') { event.preventDefault(); setActiveIndex(items.length - 1); }
    else if (event.key === 'Escape') { event.preventDefault(); close(true); }
    else if (['Enter', ' '].includes(event.key) && activeIndex >= 0) { event.preventDefault(); emit(items[activeIndex].id); }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {label && <span id={labelId} className="mb-2 block text-xs uppercase tracking-[0.24em] text-muted">{label}</span>}
      <button ref={triggerRef} type="button" role="combobox" disabled={disabled} onClick={() => !disabled && setIsOpen((open) => !open)} onKeyDown={handleKeyDown}
        aria-haspopup="listbox" aria-expanded={isOpen} aria-controls={listId} aria-labelledby={label ? labelId : undefined} aria-required={required || undefined} aria-invalid={error ? true : undefined}
        className={`flex w-full items-center justify-between rounded-2xl border bg-surface px-4 py-3 text-base outline-none transition-colors focus:ring-2 focus:ring-accent disabled:opacity-50 ${isOpen ? 'border-accent ring-2 ring-accent/30' : 'border-line'} ${selectedCategory ? 'text-ink' : 'text-muted'} ${error ? '!border-red-500 !bg-red-500/5 !ring-2 !ring-red-500/30' : ''}`}>
        <span className="truncate">{displayText}</span>
        <ChevronDown size={16} className={`ml-2 shrink-0 text-muted transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div ref={listRef} id={listId} role="listbox" tabIndex={-1} aria-labelledby={label ? labelId : undefined}
          aria-activedescendant={activeIndex >= 0 ? `${listId}-option-${activeIndex}` : undefined} onKeyDown={handleKeyDown}
          className="absolute z-50 mt-2 w-full overflow-hidden rounded-2xl border border-line bg-surface shadow-glass outline-none">
          <div className="max-h-64 overflow-y-auto py-1">
            {items.map((item, index) => {
              const selected = index === selectedIndex;
              return (
                <button id={`${listId}-option-${index}`} key={String(item.id)} type="button" role="option" tabIndex={-1} aria-selected={selected} onClick={() => emit(item.id)}
                  className={`flex w-full items-center justify-between py-2.5 text-left text-sm transition ${item.child ? 'pl-7 pr-4' : 'px-4'} ${item.parent ? 'font-semibold' : ''} ${item.hasChildren ? 'pt-3 first:pt-2.5' : ''} ${selected ? 'bg-accent/10 font-medium text-accent' : 'text-ink hover:bg-base'} ${index === activeIndex && !selected ? 'bg-base' : ''}`}>
                  <span>{item.label}</span>{selected && <Check size={13} className="shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {error && <p className="mt-2 text-sm text-accent">{error}</p>}
    </div>
  );
};

export default CategoryPicker;
